import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '@/lib/api';
import { ArrowLeft, Plus, X, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';

export default function BundleFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [allProducts, setAllProducts] = useState([]);
  const [selectedHandle, setSelectedHandle] = useState('');

  const [form, setForm] = useState({
    title: '', type: 'fixed', status: 'draft', products: [],
    discount_type: 'percentage', discount_value: 0,
    quantity_breaks: [], bogo_config: { buy_quantity: 2, get_quantity: 1, get_discount_percentage: 100 },
    mix_match_config: { min_items: 2, max_items: 4, fixed_price: 0 },
    description: '', image_url: '', start_date: '', end_date: '',
  });

  useEffect(() => {
    api.get('/products?limit=100&admin=true').then(r => setAllProducts(r.data.products || [])).catch(() => {});
    if (!isEdit) return;
    api.get(`/bundles/${id}`).then(r => { setForm(r.data); setLoading(false); }).catch(() => {
      toast.error('Bundle not found');
      navigate('/admin/bundles');
    });
  }, [id, isEdit, navigate]);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const addProduct = () => {
    if (!selectedHandle) return;
    const prod = allProducts.find(p => p.handle === selectedHandle);
    if (!prod) return;
    if (form.products.find(p => p.product_handle === selectedHandle)) { toast.error('Already added'); return; }
    set('products', [...form.products, { product_handle: prod.handle, product_title: prod.title, quantity: 1 }]);
    setSelectedHandle('');
  };

  const updateProductQty = (idx, qty) => {
    const prods = [...form.products];
    prods[idx] = { ...prods[idx], quantity: Math.max(1, parseInt(qty) || 1) };
    set('products', prods);
  };

  const removeProduct = (idx) => set('products', form.products.filter((_, i) => i !== idx));

  const addBreak = () => set('quantity_breaks', [...form.quantity_breaks, { min_qty: 1, discount_percentage: 0 }]);
  const updateBreak = (idx, key, val) => {
    const breaks = [...form.quantity_breaks];
    breaks[idx] = { ...breaks[idx], [key]: parseFloat(val) || 0 };
    set('quantity_breaks', breaks);
  };
  const removeBreak = (idx) => set('quantity_breaks', form.quantity_breaks.filter((_, i) => i !== idx));

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (form.products.length === 0) { toast.error('Add at least one product'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/bundles/${id}`, form);
        toast.success('Bundle updated');
      } else {
        await api.post('/bundles', form);
        toast.success('Bundle created');
      }
      navigate('/admin/bundles');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  if (loading) return <div className="p-12 text-center font-sans text-sm" style={{ color: 'rgba(26,26,26,0.4)' }}>Loading...</div>;

  const inputStyle = { borderColor: 'rgba(26,26,26,0.12)' };
  const labelStyle = { color: 'rgba(26,26,26,0.6)' };

  return (
    <motion.div data-testid="bundle-form-page" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <div className="flex items-center gap-4 mb-8">
        <button data-testid="back-to-bundles" onClick={() => navigate('/admin/bundles')} className="p-2 rounded-lg hover:bg-[rgba(26,26,26,0.05)] transition-colors">
          <ArrowLeft size={18} strokeWidth={1.5} />
        </button>
        <h1 className="font-serif text-2xl tracking-tight flex-1" style={{ color: '#1A1A1A' }}>
          {isEdit ? 'Edit Bundle' : 'New Bundle'}
        </h1>
        <Button data-testid="save-bundle" onClick={handleSave} disabled={saving}
          className="font-sans text-sm rounded-none px-6" style={{ backgroundColor: '#E85D3A', color: '#fff' }}>
          {saving ? 'Saving...' : 'Save Bundle'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <div className="p-6 rounded-lg border" style={{ backgroundColor: '#FDFBF7', borderColor: 'rgba(26,26,26,0.08)' }}>
            <div className="space-y-4">
              <div>
                <Label className="font-sans text-xs uppercase tracking-wider mb-1.5 block" style={labelStyle}>Bundle Title</Label>
                <Input data-testid="bundle-title" value={form.title} onChange={e => set('title', e.target.value)}
                  className="font-sans rounded-none" style={inputStyle} placeholder="e.g. Complete Skincare Ritual" />
              </div>
              <div>
                <Label className="font-sans text-xs uppercase tracking-wider mb-1.5 block" style={labelStyle}>Description</Label>
                <Textarea data-testid="bundle-description" value={form.description} onChange={e => set('description', e.target.value)}
                  className="font-sans rounded-none min-h-[80px]" style={inputStyle} />
              </div>
            </div>
          </div>

          {/* Products */}
          <div className="p-6 rounded-lg border" style={{ backgroundColor: '#FDFBF7', borderColor: 'rgba(26,26,26,0.08)' }}>
            <h3 className="font-sans text-xs uppercase tracking-wider font-medium mb-4" style={{ color: '#C4956A' }}>Products in Bundle</h3>
            <div className="flex gap-2 mb-4">
              <Select value={selectedHandle} onValueChange={setSelectedHandle}>
                <SelectTrigger data-testid="product-picker" className="flex-1 font-sans text-sm rounded-none" style={inputStyle}>
                  <SelectValue placeholder="Select a product..." />
                </SelectTrigger>
                <SelectContent>
                  {allProducts.filter(p => !form.products.find(bp => bp.product_handle === p.handle)).map(p => (
                    <SelectItem key={p.handle} value={p.handle}>{p.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button data-testid="add-bundle-product" onClick={addProduct} variant="outline" className="rounded-none font-sans text-sm" style={inputStyle}>
                <Plus size={14} />
              </Button>
            </div>
            {form.products.length === 0 ? (
              <p className="font-sans text-sm py-4 text-center" style={{ color: 'rgba(26,26,26,0.4)' }}>No products added yet.</p>
            ) : (
              <div className="space-y-2">
                {form.products.map((p, idx) => (
                  <div key={p.product_handle} className="flex items-center gap-3 p-3 rounded border" style={{ borderColor: 'rgba(26,26,26,0.06)' }}>
                    <span className="font-sans text-sm flex-1" style={{ color: '#1A1A1A' }}>{p.product_title}</span>
                    <div className="flex items-center gap-2">
                      <Label className="font-sans text-[10px] uppercase" style={labelStyle}>Qty</Label>
                      <Input type="number" min="1" value={p.quantity} onChange={e => updateProductQty(idx, e.target.value)}
                        className="w-16 font-sans text-sm rounded-none" style={inputStyle} />
                    </div>
                    <button onClick={() => removeProduct(idx)} className="p-1 rounded hover:bg-red-50">
                      <X size={14} className="text-red-500" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Type-specific config */}
          {form.type === 'quantity_break' && (
            <div className="p-6 rounded-lg border" style={{ backgroundColor: '#FDFBF7', borderColor: 'rgba(26,26,26,0.08)' }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-sans text-xs uppercase tracking-wider font-medium" style={{ color: '#C4956A' }}>Quantity Breaks</h3>
                <Button onClick={addBreak} variant="outline" size="sm" className="font-sans text-xs rounded-none gap-1" style={inputStyle}>
                  <Plus size={13} /> Add Tier
                </Button>
              </div>
              {form.quantity_breaks.map((b, idx) => (
                <div key={idx} className="flex items-center gap-3 mb-2">
                  <span className="font-sans text-sm" style={labelStyle}>Buy</span>
                  <Input type="number" value={b.min_qty} onChange={e => updateBreak(idx, 'min_qty', e.target.value)}
                    className="w-20 font-sans text-sm rounded-none" style={inputStyle} />
                  <span className="font-sans text-sm" style={labelStyle}>+, get</span>
                  <Input type="number" step="0.1" value={b.discount_percentage} onChange={e => updateBreak(idx, 'discount_percentage', e.target.value)}
                    className="w-20 font-sans text-sm rounded-none" style={inputStyle} />
                  <span className="font-sans text-sm" style={labelStyle}>% off</span>
                  <button onClick={() => removeBreak(idx)} className="p-1"><Trash2 size={13} className="text-red-500" /></button>
                </div>
              ))}
            </div>
          )}

          {form.type === 'bogo' && (
            <div className="p-6 rounded-lg border" style={{ backgroundColor: '#FDFBF7', borderColor: 'rgba(26,26,26,0.08)' }}>
              <h3 className="font-sans text-xs uppercase tracking-wider font-medium mb-4" style={{ color: '#C4956A' }}>BOGO Configuration</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="font-sans text-xs mb-1.5 block" style={labelStyle}>Buy Quantity</Label>
                  <Input type="number" value={form.bogo_config.buy_quantity}
                    onChange={e => set('bogo_config', { ...form.bogo_config, buy_quantity: parseInt(e.target.value) || 0 })}
                    className="font-sans text-sm rounded-none" style={inputStyle} />
                </div>
                <div>
                  <Label className="font-sans text-xs mb-1.5 block" style={labelStyle}>Get Quantity</Label>
                  <Input type="number" value={form.bogo_config.get_quantity}
                    onChange={e => set('bogo_config', { ...form.bogo_config, get_quantity: parseInt(e.target.value) || 0 })}
                    className="font-sans text-sm rounded-none" style={inputStyle} />
                </div>
                <div>
                  <Label className="font-sans text-xs mb-1.5 block" style={labelStyle}>Discount %</Label>
                  <Input type="number" value={form.bogo_config.get_discount_percentage}
                    onChange={e => set('bogo_config', { ...form.bogo_config, get_discount_percentage: parseFloat(e.target.value) || 0 })}
                    className="font-sans text-sm rounded-none" style={inputStyle} />
                </div>
              </div>
            </div>
          )}

          {form.type === 'mix_match' && (
            <div className="p-6 rounded-lg border" style={{ backgroundColor: '#FDFBF7', borderColor: 'rgba(26,26,26,0.08)' }}>
              <h3 className="font-sans text-xs uppercase tracking-wider font-medium mb-4" style={{ color: '#C4956A' }}>Mix & Match Configuration</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="font-sans text-xs mb-1.5 block" style={labelStyle}>Min Items</Label>
                  <Input type="number" value={form.mix_match_config.min_items}
                    onChange={e => set('mix_match_config', { ...form.mix_match_config, min_items: parseInt(e.target.value) || 0 })}
                    className="font-sans text-sm rounded-none" style={inputStyle} />
                </div>
                <div>
                  <Label className="font-sans text-xs mb-1.5 block" style={labelStyle}>Max Items</Label>
                  <Input type="number" value={form.mix_match_config.max_items}
                    onChange={e => set('mix_match_config', { ...form.mix_match_config, max_items: parseInt(e.target.value) || 0 })}
                    className="font-sans text-sm rounded-none" style={inputStyle} />
                </div>
                <div>
                  <Label className="font-sans text-xs mb-1.5 block" style={labelStyle}>Fixed Price ($)</Label>
                  <Input type="number" step="0.01" value={form.mix_match_config.fixed_price}
                    onChange={e => set('mix_match_config', { ...form.mix_match_config, fixed_price: parseFloat(e.target.value) || 0 })}
                    className="font-sans text-sm rounded-none" style={inputStyle} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right sidebar */}
        <div className="space-y-6">
          <div className="p-6 rounded-lg border" style={{ backgroundColor: '#FDFBF7', borderColor: 'rgba(26,26,26,0.08)' }}>
            <h3 className="font-sans text-xs uppercase tracking-wider font-medium mb-4" style={{ color: '#C4956A' }}>Settings</h3>
            <div className="space-y-4">
              <div>
                <Label className="font-sans text-xs mb-1.5 block" style={labelStyle}>Bundle Type</Label>
                <Select value={form.type} onValueChange={v => set('type', v)}>
                  <SelectTrigger data-testid="bundle-type" className="font-sans text-sm rounded-none" style={inputStyle}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">Fixed Bundle</SelectItem>
                    <SelectItem value="quantity_break">Quantity Break</SelectItem>
                    <SelectItem value="bogo">Buy One Get One</SelectItem>
                    <SelectItem value="mix_match">Mix & Match</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-sans text-xs mb-1.5 block" style={labelStyle}>Status</Label>
                <Select value={form.status} onValueChange={v => set('status', v)}>
                  <SelectTrigger data-testid="bundle-status" className="font-sans text-sm rounded-none" style={inputStyle}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.type === 'fixed' && (
                <>
                  <div>
                    <Label className="font-sans text-xs mb-1.5 block" style={labelStyle}>Discount Type</Label>
                    <Select value={form.discount_type} onValueChange={v => set('discount_type', v)}>
                      <SelectTrigger className="font-sans text-sm rounded-none" style={inputStyle}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentage</SelectItem>
                        <SelectItem value="fixed_amount">Fixed Amount Off</SelectItem>
                        <SelectItem value="fixed_price">Fixed Bundle Price</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="font-sans text-xs mb-1.5 block" style={labelStyle}>Discount Value</Label>
                    <Input data-testid="discount-value" type="number" step="0.01" value={form.discount_value}
                      onChange={e => set('discount_value', parseFloat(e.target.value) || 0)}
                      className="font-sans text-sm rounded-none" style={inputStyle} />
                  </div>
                </>
              )}
              <div>
                <Label className="font-sans text-xs mb-1.5 block" style={labelStyle}>Image URL</Label>
                <Input value={form.image_url} onChange={e => set('image_url', e.target.value)}
                  className="font-sans rounded-none text-sm" style={inputStyle} placeholder="Bundle image URL" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
